/**
 * Enum values and Zod schemas — canonical source of truth for the entire monorepo.
 *
 * Domain enum arrays are used in `pgEnum()` / local SQLite column definitions.
 * Validation schemas (BCP47, transport enums, etc.) are used by the API, mobile, legacy,
 * and the factories layer.
 */

import { z } from "zod";
import {
  alpha2ToAlpha3,
  alpha3ToAlpha2,
  bareCountryCode,
} from "./country-name";

// BCP-47 Language Code validation

/** Zod schema for BCP-47 language codes (e.g. en, fr, pt-BR, zh-Hans) */
export const BCP47Schema = z
  .string()
  .trim()
  .min(1)
  .refine(
    (s) => {
      try {
        // biome-ignore lint/correctness/noUnusedInstantiation: validated by constructor throw, not by reading the instance
        new Intl.Locale(s);
        return true;
      } catch {
        return false;
      }
    },
    { message: "Must be BCP-47" }
  )
  .transform((s) => s.toLowerCase());

export type BCP47 = z.infer<typeof BCP47Schema>;

/**
 * The British nationality categories other than GBR. The UK issues their
 * documents, but their holders are not UK citizens, so they name the issuer
 * and never a nationality a police filing may state.
 */
const BRITISH_NON_CITIZEN_CATEGORIES = new Set([
  "GBD",
  "GBN",
  "GBO",
  "GBP",
  "GBS",
]);

/**
 * A stored nationality in the codeset `guests.nationality` documents, ISO
 * 3166-1 alpha-3. An alpha-2 code becomes its alpha-3; anything else is kept,
 * in the upper case every MRZ code has: an alpha-3 already, or an ICAO code
 * a passport carries that ISO has no entry for (`D`, `XXA`). Those are kept
 * as the document states them rather than blanked; `alpha3ToAlpha2` reads
 * them as the country they name.
 */
export function nationalityAsAlpha3(
  code: string | null | undefined
): string | null {
  if (!code) {
    return null;
  }
  return alpha2ToAlpha3(code) ?? code.toUpperCase();
}

/** The country an MRZ country code names, as alpha-3 where ISO has one. */
export function issuerAsAlpha3(code: string | null | undefined): string | null {
  const bare = code && bareCountryCode(code);
  if (bare && BRITISH_NON_CITIZEN_CATEGORIES.has(bare)) {
    return "GBR";
  }
  return nationalityAsAlpha3(alpha3ToAlpha2(bare) ?? bare);
}

const ALPHA3_LENGTH = 3;

/**
 * What issuers and hosts put between a document number's characters: every
 * JavaScript `\s` character, and a hyphen. Spelled out so JavaScript and
 * Postgres regexes read it alike, and the database's prefilter folds as
 * {@link foldDocumentNumber} does.
 */
export const DOCUMENT_NUMBER_SEPARATORS =
  "[\\t\\n\\u000b\\f\\r \\u00a0\\u1680\\u2000-\\u200a\\u2028\\u2029\\u202f\\u205f\\u3000\\ufeff-]";

const DOCUMENT_NUMBER_SEPARATOR_RE = new RegExp(
  DOCUMENT_NUMBER_SEPARATORS,
  "gu"
);

/**
 * Upper-cases only where a character maps to one character, as Postgres's
 * `pg_c_utf8` does: "ß" stays "ß" rather than becoming "SS".
 */
function upperOneToOne(value: string): string {
  return Array.from(value, (char) => {
    const upper = char.toUpperCase();
    return upper.length === char.length ? upper : char;
  }).join("");
}

export function foldDocumentNumber(value: string | null | undefined): string {
  return upperOneToOne((value ?? "").replace(DOCUMENT_NUMBER_SEPARATOR_RE, ""));
}

interface IdentityDocument {
  documentNumber?: string | null;
  issuingCountry?: string | null;
}

/**
 * Whether a held document is the one presented. A number is unique only
 * within its issuing country, so the countries must agree whenever both sides
 * name one as three letters.
 */
export function isSameDocument(
  held: IdentityDocument,
  presented: IdentityDocument
): boolean {
  const number = foldDocumentNumber(presented.documentNumber);
  if (!number || foldDocumentNumber(held.documentNumber) !== number) {
    return false;
  }
  const heldCountry = issuerAsAlpha3(held.issuingCountry);
  const presentedCountry = issuerAsAlpha3(presented.issuingCountry);
  return (
    heldCountry?.length !== ALPHA3_LENGTH ||
    presentedCountry?.length !== ALPHA3_LENGTH ||
    heldCountry === presentedCountry
  );
}

// SHA-256 hash (64 hex characters)

export const Sha256Schema = z
  .string()
  .length(64)
  .regex(/^[a-fA-F0-9]{64}$/u, { message: "Invalid SHA-256 hash" });

/** Cached translations of message `content` by target language (BCP-47 key). */
export const messageTranslationsMapSchema = z.record(BCP47Schema, z.string());

/** What a host's guest messages are translated into when they chose nothing. */
export const DEFAULT_GUEST_TRANSLATION_LANGUAGE = "en-us";

/**
 * The key in {@link messageTranslationsMapSchema} a host's translation of a
 * guest message is stored under: their translation language, normalised, or
 * the default when it is unset or unreadable. The server writes under it and
 * the app looks a synced translation up under it.
 */
export function guestTranslationTarget(
  translationLanguage: string | null | undefined
): BCP47 {
  const parsed = BCP47Schema.safeParse(translationLanguage);
  // TODO(SUP-1116): a null target should follow the host's interface language.
  return parsed.success ? parsed.data : DEFAULT_GUEST_TRANSLATION_LANGUAGE;
}

// Unit types live in ./unit-types — plain data a public page renders without
// paying for this module's schemas.

// =========================================================
// Guest identity platform (guest_identities.channel / reachability surface)
// SMS shares the phone-number identity layer; WhatsApp is a distinct platform.
// VoIP PSTN dialing still uses `guestCallTransportValues` (“phone”).
// =========================================================

export const guestIdentityPlatformValues = [
  "whatsapp",
  "sms",
  "email",
  "instagram",
  "booking_com",
  "airbnb",
] as const;

// Order here drives the composer's transport picker order, so keep it aligned
// with `guestIdentityPlatformValues`.
export const guestMessageTransportValues = [
  "whatsapp",
  "sms",
  "email",
  "instagram",
  "booking_com",
  "airbnb",
] as const;

export const guestCallTransportValues = ["phone", "whatsapp"] as const;

export const GuestMessageTransportSchema = z.enum(guestMessageTransportValues);
export type GuestMessageTransport = z.infer<typeof GuestMessageTransportSchema>;

// Guest message delivery status (`message_status` enum). The mobile app
// inserts new outbound messages as `pending`; the PowerSync upload handler
// flips them to `sent`/`error` after the Twilio call, and the Twilio status
// webhook advances them to `delivered`/`read`. Note this is the *transport*
// delivery state — distinct from `guest_messages.read_at`, which tracks when
// a staff user read an inbound message.
export const guestMessageStatusValues = [
  "pending",
  "sent",
  "delivered",
  "read",
  "error",
] as const;

export const GuestMessageStatusSchema = z.enum(guestMessageStatusValues);
export type GuestMessageStatus = z.infer<typeof GuestMessageStatusSchema>;

export type GuestCallTransport = (typeof guestCallTransportValues)[number];

// Reservation channels & statuses

/** Why a block holds its room: out of order, an owner stay, or anything
 *  else. */
export const blockReasonValues = ["maintenance", "owner", "manual"] as const;

/**
 * Reservation origin — the coarse category indicating where a reservation
 * came from. Immutable historical fact (don't repurpose for state).
 *
 * `manual` was retired (it was an entry-method, not an origin); existing
 * rows migrate to `other`. Free-form detail (agency name, "Walk-in",
 * "Phone") lives in `reservations.source_detail`.
 */
export const reservationSourceValues = [
  "booking_com",
  "airbnb",
  // Vrbo is retired from the product (no new connections offered), but kept here
  // so historical reservations with source='vrbo' stay valid — Postgres can't
  // drop an in-use enum value. Re-add the Vrbo platform to restore it in v2.
  "vrbo",
  "direct",
  "other",
] as const;

export const ReservationSourceSchema = z.enum(reservationSourceValues);
export type ReservationSource = z.infer<typeof ReservationSourceSchema>;

// `declined` is the negative outcome of a `requested` enquiry — the host was
// asked and said no. Distinct from `cancelled`, which is a booking that existed
// and then stopped existing: a decline is demand turned away, a cancellation is
// revenue lost, and collapsing the two makes the enquiry conversion rate
// unrecoverable after the fact. Host-set only; no channel ever sends it
// (Channex has no `requested` state on its booking feed at all).
export const reservationStatusValues = [
  "requested",
  "confirmed",
  "checked_in",
  "checked_out",
  "cancelled",
  "declined",
  "no_show",
] as const;

export const ReservationStatusSchema = z.enum(reservationStatusValues);
export type ReservationStatus = z.infer<typeof ReservationStatusSchema>;

/**
 * Statuses that mean the stay is over before it started: nobody arrived and
 * nobody will. Such a reservation **releases its unit** — the nights are
 * sellable again, its assignment pin is void, and it counts toward nothing that
 * measures stays.
 *
 * Exported as one constant because these sets are denylists, and a denylist is
 * the dangerous direction: a status added later is silently *included* in
 * "still occupies a room" unless someone remembers every site. They were spread
 * across the assignment engine (twice, once as raw SQL), the channel-manager
 * batch placement, the statistics tool and self check-in, and adding `declined`
 * missed several. `test/reservation-status-parity.test.ts` fails if a new
 * hand-rolled copy appears.
 */
export const deadStayStatuses = [
  "cancelled",
  "declined",
  "no_show",
] as const satisfies readonly ReservationStatus[];

/** Whether this stay releases its unit and counts toward no stay metric. */
export function isDeadStay(status: string): boolean {
  return (deadStayStatuses as readonly string[]).includes(status);
}

/**
 * The dead stays the host's calendar does not draw: the booking was withdrawn,
 * so its nights are free and a bar would read as a clash with whoever books
 * them next. A no-show keeps its bar, as the record of an arrival that was
 * expected and missed. The same set has its guests' deposit holds released,
 * as a withdrawn booking secures nothing; a no-show keeps its hold.
 */
export const offBoardStayStatuses = [
  "cancelled",
  "declined",
] as const satisfies readonly (typeof deadStayStatuses)[number][];

/** Whether the calendar leaves this stay off the board. */
export function isOffBoardStay(status: string | undefined): boolean {
  return (offBoardStayStatuses as readonly (string | undefined)[]).includes(
    status
  );
}

/**
 * The statuses whose reservation can still reach the guest stay portal.
 *
 * `checked_out` is deliberately in: a departed guest still opens the portal for
 * their receipt, and for a registration the property is chasing after the fact.
 * Only a stay that never happened is shut out (see `deadStayStatuses`).
 *
 * Shared rather than server-side because the host app decides on the same
 * question: whether to offer a check-in link for a booking at all.
 */
export const portalActiveReservationStatuses = [
  "requested",
  "confirmed",
  "checked_in",
  "checked_out",
] as const satisfies readonly ReservationStatus[];

/** Whether a reservation in this status can still enter the stay portal. */
export function isReservationActive(status: ReservationStatus): boolean {
  return (portalActiveReservationStatuses as readonly string[]).includes(
    status
  );
}

/**
 * The statuses of a stay that is still ahead of the guest or under way.
 *
 * Narrower than `portalActiveReservationStatuses`, which keeps `checked_out` so
 * a departed guest can still reach their receipt. Here a departed stay is over.
 *
 * An allowlist, so a status added later reads as "not ongoing" until someone
 * decides otherwise — the safe direction for anything that presents a stay as
 * live.
 */
export const ongoingStayStatuses = [
  "requested",
  "confirmed",
  "checked_in",
] as const satisfies readonly ReservationStatus[];

/** Whether this stay is upcoming or under way. */
export function isOngoingStay(status: string): boolean {
  return (ongoingStayStatuses as readonly string[]).includes(status);
}

/**
 * The statuses that mean the guest actually turned up: they are in the room, or
 * they have been and gone.
 *
 * An allowlist, so a status added later reads as "has not arrived" until
 * someone decides otherwise. That is the safe direction here for a sharper
 * reason than usual: this gates what gets asserted to a government register
 * about nights slept, and a night reported for a guest who never came is a
 * misreport no surface in this product can withdraw.
 *
 * Deliberately narrower than {@link ongoingStayStatuses}, which keeps
 * `confirmed` — a booking nobody has arrived for yet.
 */
export const arrivedStayStatuses = [
  "checked_in",
  "checked_out",
] as const satisfies readonly ReservationStatus[];

/** Whether the guest has arrived, or arrived and left again. */
export function hasGuestArrived(status: string): boolean {
  return (arrivedStayStatuses as readonly string[]).includes(status);
}

// Call statuses

export const callStatusValues = [
  "queued",
  "initiated",
  "ringing",
  "in_progress",
  "completed",
  "busy",
  "failed",
  "no_answer",
  "canceled",
] as const;

// How a property's municipality was bound (ADR-0142): picked by the host.
// `address_register` is the retired address lookup's, kept for the bindings it
// made.
export const municipalityBindingMethodValues = [
  "address_register",
  "host",
] as const;

// The official registers of the facilities a regime's filing names a property
// by, imported with the municipality each stands in: Slovenia's RNO (ADR-0142).
export const registrationNumberSchemeValues = ["si_rno"] as const;
export type RegistrationNumberScheme =
  (typeof registrationNumberSchemeValues)[number];

// The code lists an authority names places by, where they are its own rather
// than the national statistics codes: the Italian police's comuni table, which
// Alloggiati Web and ROSS 1000 both file with.
export const placeCodeSchemeValues = ["it_police"] as const;
export type PlaceCodeScheme = (typeof placeCodeSchemeValues)[number];

// How one run of a reference list import ended.
export const referenceImportOutcomeValues = [
  "imported",
  "unchanged",
  "refused",
  "failed",
] as const;
export type ReferenceImportOutcome =
  (typeof referenceImportOutcomeValues)[number];

// Task types & statuses

export const taskTypeValues = ["cleaning", "maintenance"] as const;

// Sub-kind for `type = 'cleaning'` rows. Lives in shared (not in the
// planner or the mobile UI) so the two can't drift on the kind set.
export const cleaningTaskKindValues = [
  "turnover",
  "pre-checkin-refresh",
  "post-checkout",
] as const;

export const CleaningTaskKindSchema = z.enum(cleaningTaskKindValues);
export type CleaningTaskKind = z.infer<typeof CleaningTaskKindSchema>;

// =========================================================
// Task functions, rules and assignment presets
// =========================================================

/**
 * What a staffer is qualified to *do*, orthogonal to their permission tier.
 *
 * A Manager who never cleans and an Operator who only cleans differ in role,
 * not in function; a rule that needs a deep clean wants the function, not the
 * tier. Deliberately short — these are the work types the planner and the
 * assignee picker branch on, not a job-title catalogue.
 */
export const taskFunctionValues = [
  "cleaning",
  "check_in",
  "check_out",
  "maintenance",
  "concierge",
] as const;

export const TaskFunctionSchema = z.enum(taskFunctionValues);
export type TaskFunction = z.infer<typeof TaskFunctionSchema>;

/**
 * What a task rule fires on.
 *
 * The cleaning triggers are exactly `cleaningTaskKindValues`, so the planner
 * maps a plan's kind to a trigger by identity. `any` is the catch-all for a
 * property whose one preset covers every task of that type, and is the only
 * trigger a `maintenance` rule uses — maintenance tasks have no
 * reservation-boundary kind.
 */
export const taskRuleTriggerValues = [
  ...cleaningTaskKindValues,
  "any",
] as const;

export const TaskRuleTriggerSchema = z.enum(taskRuleTriggerValues);
export type TaskRuleTrigger = z.infer<typeof TaskRuleTriggerSchema>;

/**
 * How a rule picks an assignee.
 *
 * `unassigned` is today's behaviour kept as an explicit choice: the task lands
 * in the Unassigned lane for a human to place. `first_available` is the
 * availability-aware path. `specific_staff` names one person outright — the
 * "always Marie" case — and is honoured even when Marie is away, because
 * naming someone is a deliberate act and the picker still shows the warning.
 */
export const taskAssignmentPresetValues = [
  "unassigned",
  "first_available",
  "specific_staff",
] as const;

export const TaskAssignmentPresetSchema = z.enum(taskAssignmentPresetValues);
export type TaskAssignmentPreset = z.infer<typeof TaskAssignmentPresetSchema>;

// `paused` means "started, halted, resumable" — the operator hand-off case
// (A pauses, B resumes) that revert-to-pending cannot express. Distinct from
// `pending` because it stays active work and preserves `started_at` (the
// elapsed trace), where pending is a fresh, never-started slot. `task_status`
// is a Postgres pgEnum, so new values need a migration
// (`ALTER TYPE task_status ADD VALUE`).
//
// `missed` closes a clean or refresh nobody started before the guest it readied
// the room for arrived: the guest is in, so the work is no longer owed, but the
// record that it was skipped stays. Only the cleaning planner sets it.
export const taskStatusValues = [
  "pending",
  "in_progress",
  "paused",
  "completed",
  "cancelled",
  "missed",
] as const;

export const taskPriorityValues = ["low", "medium", "high"] as const;

// Statuses for work that is still "live" — assigned and not yet done or
// cancelled. `paused` counts: it's started, halted and resumable, so the
// away/time-off release, the active-task counts, and the staff task lists must
// all treat it as in-flight. Centralised so adding a status updates every
// "active task" bucket at once instead of each hardcoded set drifting.
export const activeTaskStatusValues = [
  "pending",
  "in_progress",
  "paused",
] as const;

/**
 * Closed with the work not done. Such a task holds nobody's time and owes
 * nothing: it counts toward no one's load and stays out of the calendar feed
 * and the agenda. The host calendar board still draws it, as a record.
 */
export const undoneTaskStatusValues = [
  "cancelled",
  "missed",
] as const satisfies readonly (typeof taskStatusValues)[number][];

export type ActiveTaskStatus = (typeof activeTaskStatusValues)[number];

/** Whether a task is still work to do (see `activeTaskStatusValues`). */
export function isActiveTaskStatus(status: string): status is ActiveTaskStatus {
  return (activeTaskStatusValues as readonly string[]).includes(status);
}

/** Whether a task was closed with the work not done. */
export function isUndoneTaskStatus(status: string): boolean {
  return (undoneTaskStatusValues as readonly string[]).includes(status);
}

// Unit readiness

/**
 * Whether a room can take a guest right now.
 *
 * One axis, not two: `out_of_order` dominates a cleanliness state rather than
 * sitting beside it, because a room nobody may enter is not usefully also
 * "dirty". Occupancy stays separate — it is already answerable from the
 * reservation timeline, and duplicating it here would give it a second source
 * of truth.
 *
 * `inspected` is deliberately kept even though a small property's cleaner and
 * checker are usually the same person: it is the state a door key is meant to
 * gate on, and adding an enum value later would silently move what every
 * existing reader means by "ready". Nothing forces a room THROUGH it — see
 * `isArrivalReady`, which accepts `clean` too — so a property that never runs a
 * second pass is fully served without it.
 */
export const unitReadinessValues = [
  "dirty",
  "clean",
  "inspected",
  "out_of_order",
] as const;

export const UnitReadinessSchema = z.enum(unitReadinessValues);
export type UnitReadiness = z.infer<typeof UnitReadinessSchema>;

/**
 * The states a guest may be sent into.
 *
 * Centralised so "is the room ready" is decided once: a consumer reads this
 * rather than restating the set, so a property that inspects and one that does
 * not both get the same answer.
 *
 * The arrival push and the guest portal also hold the room back while the
 * clean before the arrival is open (`unitsWithOpenArrivalClean`).
 * TODO(SUP-287, SUP-803): door codes and guest keys read arrival readiness
 * from here too.
 */
export const arrivalReadyReadinessValues = ["clean", "inspected"] as const;

export function isArrivalReady(readiness: UnitReadiness): boolean {
  return (arrivalReadyReadinessValues as readonly string[]).includes(readiness);
}

/**
 * A pre-arrival refresh prepares a room its clean has made ready, so one not
 * yet started waits while the room is dirty. Only the app holds it back: a
 * refresh finished anyway vouches for the room
 * (`applyTaskTransitionToReadiness` in the api).
 */
export function refreshWaitsForClean(
  task: { cleaningKind: CleaningTaskKind | null; status: string },
  readiness: UnitReadiness | undefined
): boolean {
  return (
    task.status === "pending" &&
    task.cleaningKind === "pre-checkin-refresh" &&
    readiness === "dirty"
  );
}

/**
 * A room held off sale by its own condition rather than by a calendar hold.
 *
 * Distinct from `blocks`, which are date-ranged and feed the channel-manager
 * ARI push. Out-of-order has no end date, so it says "not sellable now" and
 * deliberately does NOT reach the forward availability push — a room broken
 * today is not a room unbookable for the next 500 nights. Taking inventory off
 * sale for a KNOWN period stays a `blocks` row with reason `maintenance`.
 */
export function isUnitSellable(readiness: UnitReadiness): boolean {
  return readiness !== "out_of_order";
}

// =========================================================
// Task checklist (todo) items — stored as a JSONB array on `tasks.checklist`.
// A checklist is a child-list of one task (always loaded with it, never queried
// independently), so it lives on the task row rather than a separate table —
// which also means it syncs to the assigned operator for free.
// =========================================================

export const TaskChecklistItemSchema = z.object({
  done: z.boolean(),
  /** Prep-derived steps (baby cot, etc.) render emphasised so they don't get
   *  lost among the standard ones. */
  highlighted: z.boolean().optional(),
  id: z.string(),
  title: z.string(),
});
export type TaskChecklistItem = z.infer<typeof TaskChecklistItemSchema>;

export const TaskChecklistSchema = z.array(TaskChecklistItemSchema);

// AI chat roles

export const aiChatRoleValues = ["user", "assistant", "system"] as const;

// Which side of a support conversation wrote a message: the customer's staff
// member, a Vidimo support agent, or Vidimo itself (a confirmation, a check-in).

export const supportMessageAuthorValues = [
  "customer",
  "agent",
  "system",
] as const;

// Media kinds for message attachments

export const mediaKindValues = ["voice", "image", "file", "video"] as const;

// Transcription lifecycle for a voice attachment. `processing` while speech-to-
// text runs, `completed` once it lands (transcript may be empty = no speech),
// `failed` on error — which the UI surfaces with a retry.
export const transcriptionStatusValues = [
  "processing",
  "completed",
  "failed",
] as const;

// Document types (guest IDs)

export const documentTypeValues = ["passport", "id_card", "other"] as const;

// Travel-document sex marker (MRZ / `guests.sex`). Plain text in the DB, so no
// migration is tied to this; the empty-string member lives in the MRZ form
// type (`mrz.ts`), not here.
export const sexValues = ["M", "F"] as const;
export const SexSchema = z.enum(sexValues);
export type Sex = z.infer<typeof SexSchema>;

// Property access method (how a guest gets in)
//
// Drives the access card on the guest-portal home: a revealed door code, a key
// pickup at a staffed desk, a smart-lock link, or a lockbox. Stored on the
// property; the per-unit code/instructions live on `units`.

export const accessMethodValues = [
  "door_code",
  "staffed_reception",
  "smart_lock",
  "lockbox",
] as const;
export const AccessMethodSchema = z.enum(accessMethodValues);
export type AccessMethod = z.infer<typeof AccessMethodSchema>;

// Who checks a property's guests in (ADR-0146). In person, staff do, and a
// guest nobody checks in is never filed automatically; self, a guest nobody
// checks in is filed on the booking's schedule.
export const checkInModeValues = ["in_person", "self"] as const;
export const CheckInModeSchema = z.enum(checkInModeValues);
export type CheckInMode = z.infer<typeof CheckInModeSchema>;

// Lifecycle of a property's bring-your-own custom domain (Places).
// `pending_dns` → onboarding started, host hasn't pointed DNS; `verifying` →
// DNS seen, Vercel issuing the cert; `active` → cert live and host→slug mirrored
// into Edge Config; `error` → provisioning failed. Null (no row value) means the
// property has no custom domain.
export const customDomainStatusValues = [
  "pending_dns",
  "verifying",
  "active",
  "error",
] as const;
export const CustomDomainStatusSchema = z.enum(customDomainStatusValues);
export type CustomDomainStatus = z.infer<typeof CustomDomainStatusSchema>;

// Who runs a property's operations (ADR-0144). `preparing`: the host's
// incumbent system still does, so Vidimo refuses every operational effect
// (reservations, guest messages and payments, filings, channel pushes, public
// pages). `active`: Vidimo does. Only the activation workflow moves a property
// forward, and nothing moves it back.
export const propertyOperationalStateValues = ["preparing", "active"] as const;
export const PropertyOperationalStateSchema = z.enum(
  propertyOperationalStateValues
);
export type PropertyOperationalState = z.infer<
  typeof PropertyOperationalStateSchema
>;

// What the sync upload path acks a refused operational write with; the host app
// recognises it to say why the write was reverted. A fixed literal: nothing
// may be interpolated into an upload error.
export const PROPERTY_PREPARING_REFUSAL =
  "This property is preparing: Vidimo does not run its operations yet";

// What the sync upload path acks a second breakfast setup with, minted by a
// device that never saw the stored one; the host app recognises it to say why.
export const BREAKFAST_ALREADY_SET_UP_REFUSAL =
  "Breakfast is already set up for this property";

// What the sync upload path acks a stay set back to before check-in once its
// guests' arrival reached a register (ADR-0146); the host app says why.
export const REVERT_AFTER_FILING_REFUSAL =
  "These guests' arrival has already been sent to the register, so the booking can no longer be set back to before check-in.";

// The OAuth scope an MCP connection needs for the booking import (ADR-0145),
// its only write. The host consents to it separately on the consent screen,
// so a connection approved as read-only stays read-only.
export const MCP_IMPORT_SCOPE = "reservations:import";

// What an MCP connection is granted when it names no scope: who the user is
// and a refresh token, read access following from the role. Never the import.
export const MCP_DEFAULT_SCOPES = [
  "openid",
  "profile",
  "email",
  "offline_access",
] as const;

// Guest registration channel
//
// A guest registration is one capture of a guest's identity at check-in. We
// don't record HOW it was captured (scan vs typed): every path runs through the
// same review-and-confirm step, so the submitted data is what matters, and a
// no-document registration is already evident from empty document fields in the
// snapshot. `channel` records WHO captured it — the guest self-registering on
// the portal, or a staff member recording it in the app.

export const registrationChannelValues = ["guest_portal", "staff_app"] as const;

// Guest registration data-model extension
//
// Beyond the MRZ identity fields, government guest-registration regimes across
// Europe need a small, consistent set of extra fields. These cover the
// continent: residence address (Slovenia, Germany, France, Italy…), place of
// birth (Italy, France), and purpose of stay (Czechia). All optional — the
// registration system's floor (registration-systems.ts) decides which are
// mandatory for a property, and check-in prompts for them only then.

/**
 * Purpose of a guest's stay (required by Czechia's UbyPort, optional
 * elsewhere). Coarse internal buckets; a connector maps these to each
 * registry's own code set rather than us tracking every national taxonomy.
 */
export const stayPurposeValues = [
  "tourism",
  "business",
  "education",
  "medical",
  "family",
  "other",
] as const;
export const StayPurposeSchema = z.enum(stayPurposeValues);

/**
 * Where a guest's current canonical ID data was captured, recorded on the
 * `guests` row. This is the entry channel, not a verification claim:
 * `guest_portal` = the guest entered it themselves via self check-in;
 * `staff_mrz_scan` = a staff member scanned it on site. A null value means the
 * source is unknown (e.g. data predating provenance tracking). Whether the data
 * is *verified* is a separate fact carried by `idVerifiedByStaffId` /
 * `idVerifiedAt` (set only when a staff member confirms it): a `staff_mrz_scan`
 * capture is the verified state, a `guest_portal` capture is unverified until
 * staff confirm.
 */
export const idCaptureSourceValues = [
  "guest_portal",
  "staff_mrz_scan",
] as const;

/**
 * The reviewed travel-ID fields captured for a guest at check-in — the snapshot
 * stored on each `guest_registrations` row and validated on the public check-in
 * endpoint. Optional fields are omitted when blank, so a partial capture (e.g. a
 * name + DOB with no document) is represented faithfully. Mirrors the nullable
 * `guests` identity columns.
 *
 * The registration-extension fields ride here too. `residence*` and
 * `birth*` mirror durable `guests` columns (they pre-fill across stays);
 * `stayPurpose` is stay-scoped and lives only in this per-capture snapshot,
 * which is what the filing flow reads. A guest's tourist-tax reduction
 * is not part of it: the guest never states their own.
 */
export const registrationDataSchema = z.object({
  birthCountry: z.string().optional(),
  // Place of birth (durable; mirrors `guests`). `birthCountry` alpha-2.
  birthPlace: z.string().optional(),
  // ISO `YYYY-MM-DD` to match the Postgres DATE columns.
  dateOfBirth: z.iso.date().optional(),
  documentNumber: z.string().optional(),
  documentType: z.enum(documentTypeValues).optional(),
  expiryDate: z.iso.date().optional(),
  firstName: z.string().min(1),
  issuingCountry: z.string().optional(),
  lastName: z.string().min(1),
  nationality: z.string().optional(),
  personalNumber: z.string().optional(),
  residenceAddress: z.string().optional(),
  // Residence (durable; mirrors `guests`). Country is ISO 3166-1 alpha-2 (SI),
  // matching `properties.countryCode` and the rest of the platform; address is
  // a free-form line. (`nationality` / `issuingCountry` are alpha-3 only because
  // the MRZ scanner emits alpha-3.)
  residenceCountry: z.string().optional(),
  sex: SexSchema.optional(),
  stayPurpose: StayPurposeSchema.optional(),
});

export type RegistrationData = z.infer<typeof registrationDataSchema>;

// Audit entity types
//
// `rate` is one night of one rate plan: the `rate_calendar` row is the entity,
// so a night keeps its own price history after the 90-day delivery record
// behind it has been pruned.

export const auditEntityTypeValues = [
  "reservation",
  "task",
  "guest",
  "rate",
] as const;

export const AuditEntityTypeSchema = z.enum(auditEntityTypeValues);

export type AuditEntityType = z.infer<typeof AuditEntityTypeSchema>;

// Audit actions

export const auditActionValues = ["create", "update", "delete"] as const;

// Call channels

export const callChannelValues = ["phone", "whatsapp", "internal"] as const;

export const CallChannelSchema = z.enum(callChannelValues);
export type CallChannel = z.infer<typeof CallChannelSchema>;

// Call statuses (Zod schema)

export const CallStatusSchema = z.enum(callStatusValues);
export type CallStatus = z.infer<typeof CallStatusSchema>;

// Task types (Zod schema)

export const TaskTypeSchema = z.enum(taskTypeValues);
export type TaskType = z.infer<typeof TaskTypeSchema>;

// Task statuses (Zod schema)

export const TaskStatusSchema = z.enum(taskStatusValues);
export type TaskStatus = z.infer<typeof TaskStatusSchema>;

// Task priorities (Zod schema)

export const TaskPrioritySchema = z.enum(taskPriorityValues);

// AI chat roles (Zod schema)

export const AiChatRoleSchema = z.enum(aiChatRoleValues);

// Media kinds (Zod schema)

export const MediaKindSchema = z.enum(mediaKindValues);
export type MediaKind = z.infer<typeof MediaKindSchema>;

export const TranscriptionStatusSchema = z.enum(transcriptionStatusValues);
export type TranscriptionStatus = z.infer<typeof TranscriptionStatusSchema>;

// Document types (Zod schema)

export const DocumentTypeSchema = z.enum(documentTypeValues);
export type DocumentType = z.infer<typeof DocumentTypeSchema>;

// Staff message kinds. Anything other than "text" renders as its own bubble
// and carries a nullable FK to the row it points at, so the card can hydrate
// live from local data instead of freezing a snapshot at send time.

export const staffMessageKindValues = [
  "text",
  "call",
  "reservation",
  "task",
  "guest",
] as const;

export const StaffMessageKindSchema = z.enum(staffMessageKindValues);
export type StaffMessageKind = z.infer<typeof StaffMessageKindSchema>;

/** The kinds that reference an entity row and render as a share card. */
export const staffMessageEntityKindValues = [
  "reservation",
  "task",
  "guest",
] as const;

export const StaffMessageEntityKindSchema = z.enum(
  staffMessageEntityKindValues
);

export type StaffMessageEntityKind = z.infer<
  typeof StaffMessageEntityKindSchema
>;

// Integration providers
//
// Source of truth lives in `./platforms.ts` (`integrationProviderValues`,
// `integrationPlatformValues`). Re-exported through `./index.ts`.

// =========================================================
// Property tenancy invitation status — matches the BetterAuth org plugin's
// emitted values (see better-auth/plugins/organization/adapter.mjs:760).
// Defaults to "pending" on `auth.api.createInvitation`; transitions to
// "accepted" / "rejected" via the accept/reject routes, or "canceled" via
// the explicit cancel route.
// =========================================================

export const orgMemberInvitationStatusValues = [
  "pending",
  "accepted",
  "rejected",
  "canceled",
] as const;

export const OrgMemberInvitationStatusSchema = z.enum(
  orgMemberInvitationStatusValues
);

// Push notifications
//
// The device-token transport split: `@capacitor/push-notifications` yields a
// native APNs token on iOS and an FCM token on Android, which the server sends
// to directly (APNs) / via FCM HTTP v1 (Android).

export const pushPlatformValues = ["ios", "android"] as const;

export const PushPlatformSchema = z.enum(pushPlatformValues);
export type PushPlatform = z.infer<typeof PushPlatformSchema>;

// Token kind discriminator. `alert` is the standard notification token (this
// issue). `live_activity` is the per-activity ActivityKit push token,
// which shares the APNs transport but a different push-type/topic — folded in
// here so both token kinds live in one table.
export const pushTokenKindValues = ["alert", "live_activity"] as const;

// Android FCM notification channel. The channel must be created on the
// device (client) and named in the FCM payload (server) by the SAME id, or
// Android O+ drops the notification — so the id lives here, shared by both.
// IMPORTANCE_HIGH gives a heads-up banner.
export const ANDROID_PUSH_CHANNEL_ID = "superhost-default";

// What a push carries for its tap. The app opens the record the ids name,
// whatever the `kind`, so a new push that names its record needs no app
// change; `kind` only picks the list for a push that names none. String
// values only: FCM data is string→string.
export const PushDataSchema = z.object({
  chatId: z.string().optional(),
  chatType: z.string().optional(),
  /** With `reservationId`: that guest's registration on the stay. */
  guestId: z.string().optional(),
  kind: z.string(),
  propertyId: z.string().optional(),
  reservationId: z.string().optional(),
  reservationUnitId: z.string().optional(),
  taskId: z.string().optional(),
});
export type PushData = z.infer<typeof PushDataSchema>;

// Server-side mirror of the on-device notification toggles. The dispatcher
// checks the matching key before sending a push of that kind.
export const notificationPreferenceKeyValues = [
  "messages",
  "tasks",
  "reservations",
  "compliance",
] as const;

export const NotificationPreferenceKeySchema = z.enum(
  notificationPreferenceKeyValues
);
export type NotificationPreferenceKey = z.infer<
  typeof NotificationPreferenceKeySchema
>;
